/**
 * BANKER_SET_POT (cee-lo) — banker drops bills into the pot zone.
 * Other players watch with closed wallets.
 *
 * UI surface on entry:
 *   - profile cards in betting mode (different framing than shooting)
 *   - camera lerps to the betting anchor
 *   - wallet open for the local player IFF they're the banker;
 *     closed for everyone else
 *   - instruction banner: "Banker's Bet" / per-side copy
 *   - no countdown, no lock-in, no opponent dice
 *
 * onUpdate is intentionally narrow — banker doesn't shift mid-state,
 * so the camera/wallet/instruction setup from onEnter stays valid.
 * We just keep the profile roster and dice-result icons synced (for
 * late joiners and balance updates).
 *
 * onExit is empty — FADING is the next state and it reuses the betting
 * camera + wallet (its enter is idempotent), so there's no teardown
 * to do here. Transitioning out to a non-betting state would be
 * handled by that state's exitBettingMode call.
 */

import type { GameUpdate } from '@sdl/shared';
import { State } from '../State.js';

export class BankerSetPotState extends State {
  async onEnter(update: GameUpdate): Promise<void> {
    const game = this.game;
    const localId = game.getLocalPlayerId();
    const isLocalBanker = !!localId && update.bankerId === localId;

    game.playerProfilesOverlay?.show();
    game.playerProfilesOverlay?.setMode('betting');
    game.bettingHUD3D?.setLockInVisible(false);
    game.countdownOverlay?.stop();

    // Camera + wallet — composite ordered call (camera first, wallet
    // second, with input-gate refresh in between).
    await game.enterBettingMode({
      open: isLocalBanker,
      animated: update.animated,
      previousState: update.previousState,
    });

    game.instructionBanner?.show(
      {
        title: "Banker's Bet",
        text: isLocalBanker
          ? 'You are the bank. Place your bet'
          : 'Wait for the banker to place a bet',
      },
      update.animated,
    );

    // Round boundary — wipe the previous round's dice-result icons
    // (banker / shooter showdown, tie marks, HI_LO single-die outcomes)
    // before _checkPlayers reconciles fresh data. Same explicit clear as
    // craps SHOOTER_SET_STACK — see clearRoundResults docstring.
    game.clearRoundResults(update.animated);
    this._updateProfiles(update);
    await this._checkPlayers(update);
  }

  async onUpdate(update: GameUpdate): Promise<void> {
    // Banker is fixed for the whole round once HI_LO resolves, so the
    // camera/wallet/instruction set in onEnter stay valid. Just refresh
    // profile data + dice-result icons.
    this._updateProfiles(update);
    await this._checkPlayers(update);
  }

  async onExit(_update: GameUpdate): Promise<void> {
    // Clear the BETTING activity pill — FADING reuses the camera +
    // wallet on its own entry, so nothing else to tear down.
    this._clearBettingActivity();
  }
}
